import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import StatCard from "./components/StatCard";
import DateRangeSelector from "./components/DateRangeSelector";
import RecentActivity from "./components/RecentActivity";
import SystemHealth from "./components/SystemHealth";
import ContentOverview from "./components/ContentOverview";
import UserGrowthChart from "./components/UserGrowthChart";
import Icon from "../../components/AppIcon";
const apiUrl = import.meta.env.VITE_API_URL;

const DashboardOverview = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState("week");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Mock data for dashboard
  const mockDashboardData = {
    totalUsers: 12458,
    activeUsers: 8721,
    contentItems: 3254,
    systemHealth: 98,
    userGrowth: [
      { date: "2023-01", users: 9800 },
      { date: "2023-02", users: 10200 },
      { date: "2023-03", users: 10700 },
      { date: "2023-04", users: 11100 },
      { date: "2023-05", users: 11500 },
      { date: "2023-06", users: 11800 },
      { date: "2023-07", users: 12100 },
      { date: "2023-08", users: 12458 },
    ],
    recentActivity: [
      {
        id: 1,
        type: "user_signup",
        user: "Sarah Johnson",
        timestamp: "2023-08-15T14:32:00Z",
        details: "New user registered via email",
        avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      },
      {
        id: 2,
        type: "content_created",
        user: "Michael Chen",
        timestamp: "2023-08-15T13:18:00Z",
        details: "Created new article: \'Getting Started with React'",
        avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      },
      {
        id: 3,
        type: "system_alert",
        user: "System",
        timestamp: "2023-08-15T12:05:00Z",
        details: "Database backup completed successfully",
        avatar: null,
      },
      {
        id: 4,
        type: "user_login",
        user: "Alex Rodriguez",
        timestamp: "2023-08-15T11:47:00Z",
        details: "User logged in from new device",
        avatar: "https://randomuser.me/api/portraits/men/75.jpg",
      },
      {
        id: 5,
        type: "content_updated",
        user: "Emma Wilson",
        timestamp: "2023-08-15T10:23:00Z",
        details: "Updated user documentation for API v2",
        avatar: "https://randomuser.me/api/portraits/women/22.jpg",
      },
    ],
    systemHealthMetrics: [
      { name: "API Response Time", value: 235, unit: "ms", status: "good" },
      { name: "Server CPU", value: 42, unit: "%", status: "good" },
      { name: "Memory Usage", value: 68, unit: "%", status: "warning" },
      { name: "Disk Space", value: 76, unit: "%", status: "good" },
      { name: "Database Connections", value: 128, unit: "", status: "good" },
    ],
    contentOverview: {
      articles: 1245,
      videos: 682,
      downloads: 327,
      products: 1000,
    },
  };

  // Simulate data loading
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/admin-user/count`);
        const json = await res.json();
        // const googleAnalyticsRes = await fetch(`${apiUrl}/api/admin-user/google-analytics/count`);
        // const googleAnalyticsJson = await googleAnalyticsRes.json();
        if (res.ok) {
          setDashboardData({ googleAnalytics: 0, totalUsers: json.total })
        } else {
          throw new Error(json.error || "Failed to fetch data.");
        }

        setError(null);
      } catch (err) {
        // setToast({ show: true, message: 'Failed to fetch data.', type: "error" });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    // Set up refresh interval (every 60s)
    const refreshInterval = setInterval(() => {
      loadData();
    }, 60000);

    return () => clearInterval(refreshInterval);
  }, [dateRange]);

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  // Handle retry on error
  const handleRetry = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin-user/count`);
      const json = await res.json();
      // const googleAnalyticsRes = await fetch(`${apiUrl}/api/admin-user/google-analytics/count`);
      // const googleAnalyticsJson = await googleAnalyticsRes.json();
      if (res.ok) {
        setDashboardData({ googleAnalytics: 0, totalUsers: json.total })
      } else {
        throw new Error(json.error || "Failed to fetch data.");
      }

      setError(null);
    } catch (err) {
      setToast({ show: true, message: 'Failed to fetch data.', type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        variant="with-categories"
        onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
      />

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? "md:ml-16" : "md:ml-64"
          }`}
      >
        {/* Header */}
        <Header isCollapsed={sidebarCollapsed} />

        {/* Main Content Area */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto p-4 md:p-6 bg-background"
        >
          {/* Dashboard Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
              <p className="mt-1 text-sm text-gray-500">
                Monitor key metrics and system performance
              </p>
            </div>
            {/* <div className="mt-4 md:mt-0 flex items-center">
              <DateRangeSelector
                selected={dateRange}
                onChange={handleDateRangeChange}
              />
              <Link
                to="/data-table-view"
                className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Icon name="Table" size={16} className="mr-2" />
                View Data Tables
              </Link>
            </div> */}
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 bg-error bg-opacity-10 border border-error border-opacity-20 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Icon
                    name="AlertCircle"
                    className="h-5 w-5 text-error"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                  <p className="text-sm text-error">{error}</p>
                  <p className="mt-3 text-sm md:mt-0 md:ml-6">
                    <button
                      onClick={handleRetry}
                      className="whitespace-nowrap font-medium text-error hover:text-error-700 focus:outline-none focus:underline"
                    >
                      Retry
                      <span aria-hidden="true"> &rarr;</span>
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              isShowTrend={true}
              onClick={() => { navigate('/users') }}
              title="Total Users"
              value={isLoading ? null : dashboardData?.totalUsers}
              icon="Users"
              trend={+5.2}
              isLoading={isLoading}
            />
            <StatCard
              isShowTrend={false}
              onClick={() => { navigate('/analytics') }}
              title="Analytics Data"
              value={isLoading ? null : dashboardData?.googleAnalytics}
              icon="ChartNoAxesCombined"
              trend={+5.2}
              isLoading={isLoading}
            />
            {/* <StatCard
              title="Active Users"
              value={isLoading ? null : dashboardData?.activeUsers}
              icon="UserCheck"
              trend={+3.1}
              isLoading={isLoading}
            />
            <StatCard
              title="Content Items"
              value={isLoading ? null : dashboardData?.contentItems}
              icon="FileText"
              trend={+12.5}
              isLoading={isLoading}
            />
            <StatCard
              title="System Health"
              value={isLoading ? null : dashboardData?.systemHealth}
              suffix="%"
              icon="Activity"
              trend={-0.3}
              isLoading={isLoading}
            /> */}
          </div>

          {/* Charts and Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* User Growth Chart - 2/3 width on large screens */}
            {/* <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <UserGrowthChart
                data={isLoading ? [] : dashboardData?.userGrowth}
                isLoading={isLoading}
              />
            </div> */}

            {/* Recent Activity - 1/3 width on large screens */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <RecentActivity
                activities={isLoading ? [] : dashboardData?.recentActivity}
                isLoading={isLoading}
              />
            </div> */}
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Health */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <SystemHealth
                metrics={isLoading ? [] : dashboardData?.systemHealthMetrics}
                isLoading={isLoading}
              />
            </div> */}

            {/* Content Overview */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <ContentOverview
                data={isLoading ? null : dashboardData?.contentOverview}
                isLoading={isLoading}
              />
            </div> */}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 p-4 text-center text-sm text-gray-500">
          <p>
            &copy; {new Date().getFullYear()} EternitAI. All rights reserved.
            reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default DashboardOverview;